import marvelEmitter from '@marvelapp/react-ab-test/lib/emitter';
import { withRouter } from 'next/router';
import nookies from 'nookies';
import { darken } from 'polished';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import styled, { css } from 'styled-components';
import { withAccount } from '/imports/core/api/accounts/accountContext';
import { withResponsiveContext } from '/imports/core/api/responsiveContext';
import { withIntl } from '/imports/core/api/useIntl';
import { withTracking } from '/imports/core/hooks/useTracking';
import { CheckIcon } from '/imports/core/ui/assets';
import Button from '/imports/core/ui/atoms/Button';
import Flex from '/imports/core/ui/atoms/Flex';
import {
  FAKE_TEMPLATES,
  FEATURED_TEMPLATES,
  FEATURED_TEMPLATES_MESSAGES,
  TEMPLATES,
} from '/imports/generator/api/constants';
import { randomizeTemplates } from '/imports/generator/api/helpers';
import { COLOR_PREVIEW_TEMPLATES_WON_LOCALE, INDEX_BY_NAME } from '/imports/onboarding/api/constants';
import { NextIcon, PrevIcon } from '/imports/onboarding/ui/assets';
import { OnboardingSliderTemplate as Template } from '/imports/onboarding/ui/atoms';
import { BREAKPOINTS } from '/styles/variables';
import { useSelectStore } from '/zustand/SelectStore';

@withTracking
@withRouter
@withIntl
@withResponsiveContext
@withAccount
class OnboardingSlider extends PureComponent {
  static propTypes = {
    onSelect: PropTypes.func.isRequired,
    isPopupOpen: PropTypes.bool,
  };

  state = {
    width: 0,
    height: 0,
    current: 0,
    templates: [],
    loading: true,
    didSetInitialSize: false,
    selectedColors: null,
  };

  updateDimensions = () => {
    if (!this._wrapper) return;
    const addressBarHeight = window.innerHeight - (window.visualViewport?.height || window.innerHeight);
    if (this.didSetInitialSize && addressBarHeight > 0) return;
    const top = this._wrapper.offsetTop;
    const wHeight = window.innerHeight;
    const footer = (window.innerWidth > BREAKPOINTS.md && 102) || 240;
    const height = Math.max(300, wHeight - top - 120 - footer);
    const width = (height / 280) * 190;
    this.didSetInitialSize = true;
    this.setState({ width, height });
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.currentUser !== this.props.currentUser) {
      const templates = this.shuffleTemplates();
      const filteredTemplates = this.filterTemplatesByRole(templates);
      this.setState({
        templates: filteredTemplates,
      });
    }
    const isWithTemplateColorV2 =
      marvelEmitter.getActiveVariant('exp_resume_template_color_v2') === 'with_template_color';

    const isWithTemplateColor = COLOR_PREVIEW_TEMPLATES_WON_LOCALE.includes(this.props.locale) || isWithTemplateColorV2;

    if (prevState.current !== this.state.current && isWithTemplateColor) {
      const { trackEvent } = this.props;
      const { setSelectedColorTemplate } = useSelectStore.getState();
      const currentTemplate = this.state.templates[this.state.current];
      const template = this.state.templates.find((t) => t.id === currentTemplate?.id);
      const idx = INDEX_BY_NAME[template?.name] ?? 0;
      const data = {
        hasColor: template?.colorCode?.[idx],
        colorName: template?.colors?.[idx],
      };
      trackEvent('color_click', {
        template: template?.name,
        color: template?.colors?.[idx],
      });
      setSelectedColorTemplate(data);
      this.setState({
        selectedColors: data,
      });
    }
  }

  componentDidMount() {
    const { sessionStartHandler, trackEvent, host, locale, isMobile } = this.props;
    sessionStartHandler();
    let templates = this.shuffleTemplates();
    templates = this.filterTemplatesByRole(templates);
    let obj = {
      random_default_template: templates[0].name,
    };
    obj.exp_job_track = marvelEmitter.getActiveVariant('job-tracking');
    obj.exp_template_budapest = marvelEmitter.getActiveVariant('exp_template_budapest');

    this.setState({
      templates,
    });

    window.addEventListener('resize', this.updateDimensions);

    try {
      document.getElementsByTagName('body')[0].classList.remove('_container');
    } catch (error) {
      console.error("Can't find body tag");
    }
    this.setState({ loading: false, templates });

    const isWithTemplateColorV2 =
      marvelEmitter.getActiveVariant('exp_resume_template_color_v2') === 'with_template_color';

    const isWithTemplateColor = COLOR_PREVIEW_TEMPLATES_WON_LOCALE.includes(locale) || isWithTemplateColorV2;

    if (isWithTemplateColor) {
      const { setSelectedColorTemplate } = useSelectStore.getState();
      const currentTemplate = templates[this.state.current];
      const template = templates?.find((t) => t?.id === currentTemplate?.id);
      const data = {
        hasColor: template?.colorCode[0],
        colorName: template?.colors[0],
      };

      setSelectedColorTemplate(data);
      this.setState({
        selectedColors: data,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
    try {
      document.getElementsByTagName('body')[0].classList.remove('_container');
    } catch (error) {
      console.error("Can't find body tag");
    }
  }

  shuffleTemplates() {
    const { locale, isMobile, currentUser } = this.props;
    const { agent_client_id } = nookies.get({});
    const isAgentOrAdmin =
      (currentUser && (currentUser.role === 'agent' || currentUser.role === 'admin')) ||
      (agent_client_id !== undefined && agent_client_id !== null);
    const activeBruneiExp = marvelEmitter.getActiveVariant('exp_new_template') === 'with_brunei';
    let visibleTemplates = TEMPLATES.filter((template) => !template.hidden);
    let randomizedTemplate = randomizeTemplates(
      visibleTemplates,
      FAKE_TEMPLATES,
      FEATURED_TEMPLATES.slice(0, 2),
      FEATURED_TEMPLATES.slice(2),
    ).filter((template) => {
      if (typeof template.hiddenOnSlider == 'function') {
        return !template.hiddenOnSlider({ locale: this.props.locale, activeBruneiExp, isMobile, currentUser });
      }
      return !template.hiddenOnSlider;
    });

    const caliIndex = randomizedTemplate.findIndex((template) => template.id === 'cali');
    const limaIndex = randomizedTemplate.findIndex((template) => template.id === 'lima');
    const caliTemplate = randomizedTemplate.splice(caliIndex, 1)[0];
    const limaTemplate = randomizedTemplate.splice(limaIndex > caliIndex ? limaIndex - 1 : limaIndex, 1)[0];
    randomizedTemplate.splice(3, 0, limaTemplate);
    randomizedTemplate.splice(6, 0, caliTemplate);
    return randomizedTemplate;
  }

  getTemplateById(templates, id) {
    return templates.find((template) => template.id === id);
  }

  filterTemplatesByRole(templates) {
    const { currentUser, locale } = this.props;
    const { agent_client_id } = nookies.get({});

    const isAgentOrAdmin = currentUser && (currentUser.role === 'agent' || currentUser.role === 'admin');
    if ((agent_client_id !== undefined && agent_client_id !== null) || isAgentOrAdmin) {
      return templates;
    } else {
      return templates.filter((template) => template?.id !== 'budapest-v2');
    }
  }

  getWrapperRef = (node) => {
    this._wrapper = node;
    this.updateDimensions();
  };

  getSlider = (node) => {
    this._slider = node;
  };

  next = () => {
    this._slider.slickNext();
  };

  previous = () => {
    this._slider.slickPrev();
  };

  onTemplateSelect = (index) => {
    this.setState({ current: index });
  };

  handleColorSelect = (templateId, colorIndex) => {
    const { setSelectedColorTemplate } = useSelectStore.getState();
    const { trackEvent } = this.props;

    const template = this.state.templates.find((t) => t.id === templateId);
    const data = {
      hasColor: template?.colorCode[colorIndex],
      colorName: template?.colors[colorIndex],
    };
    trackEvent('color_click', {
      template: template?.name,
      color: template?.colors[colorIndex],
    });
    setSelectedColorTemplate(data);
    this.setState({
      selectedColors: data,
    });
  };

  render() {
    const { onSelect, isPopupOpen, t, onSkip, isMobile, locale } = this.props;
    if (this.state.loading) return null;
    const settings = {
      dots: false,
      arrows: false,
      infinite: true,
      speed: 170,
      slidesToShow: 1,
      slidesToScroll: 1,
      adaptiveHeight: true,
      centerMode: true,
      variableWidth: true,
      focusOnSelect: true,
      beforeChange: (current, next) => this.setState({ current: next }),
    };
    const { templates, current, selectedColors } = this.state;
    const featured_experiment_templates = FEATURED_TEMPLATES;
    const currentTemplate = templates[current];
    const isATSResume = currentTemplate?.name == 'Tokyo';
    const isWithTemplateColorV2 =
      marvelEmitter.getActiveVariant('exp_resume_template_color_v2') === 'with_template_color';

    const isWithTemplateColor = COLOR_PREVIEW_TEMPLATES_WON_LOCALE.includes(locale) || isWithTemplateColorV2;

    const renderColorSelector = () => {
      if (!currentTemplate?.colorCode) return null;
      return (
        <ColorSelector $justifyContent="center">
          {currentTemplate?.colorCode?.map((color, index) => {
            const isSelected = color === selectedColors?.hasColor;
            return (
              <ColorRound
                key={`${currentTemplate.id}-color-${index}`}
                color={color}
                isSelected={isSelected}
                onClick={() => this.handleColorSelect(currentTemplate.id, index)}
              >
                {isSelected && (
                  <Checked checked={isSelected}>
                    <CheckIcon color={isATSResume ? '#000' : '#fff'} />
                  </Checked>
                )}
              </ColorRound>
            );
          })}
        </ColorSelector>
      );
    };
    return (
      <>
        {isWithTemplateColor && renderColorSelector()}
        <SliderWrapper ref={this.getWrapperRef} $isNewVar={isWithTemplateColor}>
          <SliderControlLeft withoutBackground={isPopupOpen}>
            <SliderControlButton onClick={this.previous} aria-label={t('resume_options_btn_previous')} role="button">
              <PrevIcon />
            </SliderControlButton>
          </SliderControlLeft>
          <SliderControlRight withoutBackground={isPopupOpen}>
            <SliderControlButton onClick={this.next} aria-label={t('resume_options_btn_next')} role="button">
              <NextIcon />
            </SliderControlButton>
          </SliderControlRight>
          <Slider ref={this.getSlider} {...settings}>
            {templates.map((template, i) => {
              const featured = !template?.hideLabel && featured_experiment_templates.includes(template.id);
              let message = '';
              if (featured) {
                switch (i) {
                  case 0:
                    message = FEATURED_TEMPLATES_MESSAGES[0];
                    break;
                  case 1:
                    message = FEATURED_TEMPLATES_MESSAGES[1];
                    break;
                  case templates.length - 1:
                    message = FEATURED_TEMPLATES_MESSAGES[2];
                    break;
                  default:
                    break;
                }
              }

              const idx = INDEX_BY_NAME[template?.name] ?? 0;

              const defultColor = template?.colors[idx];

              return (
                <Template
                  selected={i === this.state.current}
                  key={`${template.image || template.id}`}
                  {...template}
                  {...this.state}
                  featured={featured}
                  message={message}
                  onSelect={(template) => {
                    onSelect(template, template, false, this.state.selectedColors);
                  }}
                  onTemplateSelect={this.onTemplateSelect}
                  selectedColor={i === this.state.current ? this.state.selectedColors?.colorName : defultColor}
                  isWithTemplateColor={isWithTemplateColor}
                />
              );
            })}
          </Slider>
          {isMobile && (
            <ChooseLaterBtn onClick={onSkip} $alignItems="center" $justifyContent="center">
              {t('onboarding.choose_later_cta')}
            </ChooseLaterBtn>
          )}
        </SliderWrapper>
      </>
    );
  }
}

const ChooseLaterBtn = styled(Flex)`
  color: #1688fe;
  text-align: center;
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 16px;
  line-height: 22px;
  z-index: 9;
  text-decoration: underline;
  padding-top: 4px;
  cursor: pointer;
  width: fit-content;
  margin: 0 auto;
  margin-top: 19px;
`;

const ColorRound = styled(Flex)`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  ${({ isSelected, color }) => css`
    background-color: ${color};
    ${isSelected &&
    css`
      border: 2px solid #35393d;
    `}
  `}
`;

const ColorSelector = styled(Flex)`
  margin-top: 20px;
  gap: 12px;
`;

const SliderWrapper = styled.section`
  text-align: center;
  position: relative;
  margin-top: 70px;

  ${({ theme }) => theme.max('md')`
    margin: 0 -20px;
  `}

  ${({ theme }) => theme.min('md')`
    .slick-list {
      overflow: initial;
    }
  `}

  margin-top: 0;
  padding: 45px 0 20px;

  ${({ $isNewVar }) => css`
    ${$isNewVar &&
    css`
      padding-top: 30px;
    `}
  `}

  & .slick-list {
    overflow: initial;
  }

  ${({ theme }) => theme.max('md')`
      padding: 0 0 20px;
    `}

  .slick-track {
    display: flex;
  }
`;

const SliderControl = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 160px;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.max('md')`
    display: none;
  `}
`;

const SliderControlLeft = styled(SliderControl)`
  left: -20px;
  justify-content: flex-start;
  background: ${({ theme }) =>
    `linear-gradient(to left, rgba(255, 255, 255, 0.001), ${theme.colors.gray.light2} 55%);`};
  ${({ withoutBackground }) =>
    withoutBackground &&
    css`
      background: transparent;
      & > button {
        display: none;
      }
    `}
`;

const SliderControlRight = styled(SliderControl)`
  right: -20px;
  justify-content: flex-end;
  background: ${({ theme }) =>
    `linear-gradient(to right, rgba(255, 255, 255, 0.001), ${theme.colors.gray.light2} 55%);`};
  ${({ withoutBackground }) =>
    withoutBackground &&
    css`
      background: transparent;
      & > button {
        display: none;
      }
    `}
`;

const SliderControlButton = styled((p) => <Button unstyled {...p} />)`
  width: 52px;
  height: 52px;
  border-radius: 52px;
  margin: 36px;
  cursor: pointer;
  color: white;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  user-select: none;
  font-size: 32px;
  background: ${({ theme }) => theme.colors.black2};
  &:hover {
    background: ${({ theme }) => darken(0.05, theme.colors.black2)};
    border-color: ${({ theme }) => darken(0.05, theme.colors.black2)};
  }
`;

const Checked = styled(Flex)`
  position: absolute;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%) scale(1);
  svg {
    width: 15px;
    height: 15px;
  }
`;

export default OnboardingSlider;
